import { type ReactNode, createContext, useContext } from "react";
import type { ParsedAppRoute } from "../parse-app-route";

export type AppAgentNavigateDestination =
	| "ask"
	| "library"
	| "whiteboards"
	| "trash"
	| "draft"
	| "profile"
	| "whiteboard"
	| "card";

export type AppAgentNavigateAppInput = {
	destination: AppAgentNavigateDestination;
	whiteboardId?: string;
	cardId?: string;
};

export type AppAgentNavigateAppResult =
	| {
			ok: true;
			destination: AppAgentNavigateDestination;
			pathname: string;
			whiteboardId?: string;
			cardId?: string;
	  }
	| {
			ok: false;
			error: string;
	  };

export type AppAgentActions = {
	getCurrentRoute: () => Promise<ParsedAppRoute>;
	navigateApp: (
		input: AppAgentNavigateAppInput,
	) => Promise<AppAgentNavigateAppResult>;
};

export type AppAgentContextValue = {
	actions: AppAgentActions | null;
};

const AppAgentContext = createContext<AppAgentContextValue | null>(null);

AppAgentContext.displayName = "AppAgentContext";

export function AppAgentProvider({
	children,
	value,
}: {
	children: ReactNode;
	value: AppAgentContextValue;
}) {
	return (
		<AppAgentContext.Provider value={value}>
			{children}
		</AppAgentContext.Provider>
	);
}

export function useAppAgentContext() {
	return useContext(AppAgentContext);
}

export function useAppAgentActionsRequired() {
	const context = useAppAgentContext();
	if (!context?.actions) {
		throw new Error(
			"useAppAgentActionsRequired must be used within AppAgentProvider",
		);
	}

	return context.actions;
}
